import React, { lazy, Suspense, useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Alert, Box, Button, CircularProgress, Link, Snackbar, TextField, Typography } from '@mui/material';
import { Share, Upload } from 'lucide-react';
import Page, { cardSx } from '../components/Page';
import Logo from '../components/Logo';
import Mockup from '../components/Mockup';
import SuccessCheck from '../components/SuccessCheck';
import { cleanText, LIMITS, normalizeEmail, validateWaitlist } from '../lib/validation';
import { friendlyError, joinWaitlist, preloadBackend } from '../lib/backend';
import { motion as m } from '../brand/tokens';

const PrivacyDialog = lazy(() => import('../components/PrivacyDialog'));

const EMPTY = { firstName: '', lastName: '', email: '' };

async function shareGroni() {
  const data = { title: 'Groni', text: 'Join me on the Groni waitlist — find the perfect ratio.', url: window.location.origin };
  try {
    if (navigator.share) await navigator.share(data);
    else await navigator.clipboard.writeText(data.url);
    return navigator.share ? null : 'Link copied';
  } catch {
    return null; // Share sheet dismissed.
  }
}

function WaitlistPage() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { status: 'joined' | 'exists', firstName }
  const [toast, setToast] = useState(null); // { message, severity }
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateWaitlist(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    try {
      const entry = {
        firstName: cleanText(form.firstName, LIMITS.name),
        lastName: cleanText(form.lastName, LIMITS.name),
        email: normalizeEmail(form.email),
      };
      const status = await joinWaitlist(entry);
      setResult({ status, firstName: entry.firstName });
      setForm(EMPTY);
    } catch (error) {
      console.error('Waitlist error:', error);
      setToast({ message: friendlyError(error), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldProps = (name) => ({
    name,
    value: form[name],
    onChange: handleChange,
    error: Boolean(errors[name]),
    helperText: errors[name],
    fullWidth: true,
    disabled: submitting,
  });

  return (
    <Page>
      {/* Desktop: mockups either side of the form. */}
      <Box aria-hidden="true" sx={{ display: { xs: 'none', lg: 'block' } }}>
        <Mockup
          name="mockup-feed"
          alt=""
          eager
          sx={{ position: 'fixed', left: '3vw', top: '52%', transform: 'translateY(-45%)', width: 'min(25vw, 420px)', pointerEvents: 'none' }}
        />
        <Mockup
          name="mockup-profile"
          alt=""
          eager
          sx={{ position: 'fixed', right: '4vw', top: '52%', transform: 'translateY(-45%)', width: 'min(22vw, 360px)', pointerEvents: 'none', opacity: 0.92 }}
        />
      </Box>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Logo width={168} animation="rise" sx={{ mx: 'auto', mb: 3 }} />
        <Typography variant="h2" component="h1" sx={{ fontSize: { xs: 38, sm: 52 } }}>
          Find the perfect ratio.
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: { xs: 17, sm: 18 }, maxWidth: 440, mx: 'auto' }}>
          Rate, rank and share the world's best Negronis. Join the waitlist to be first in when Groni launches.
        </Typography>
      </Box>

      <Box sx={cardSx}>
        <AnimatePresence mode="wait" initial={false}>
          {result ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={m.spring.snappy}
              style={{ textAlign: 'center', padding: '16px 0' }}
            >
              <SuccessCheck />
              <Typography variant="h5" component="h2" sx={{ mt: 3 }} role="status">
                {result.status === 'exists' ? "You're already on the list" : `You're on the list, ${result.firstName}!`}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                {result.status === 'exists'
                  ? "No need to sign up twice — we'll email you at launch."
                  : "We'll email you the moment Groni opens. Cheers!"}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Share size={18} />}
                  onClick={async () => {
                    const message = await shareGroni();
                    if (message) setToast({ message, severity: 'success' });
                  }}
                >
                  Tell a friend
                </Button>
                <Button component={RouterLink} to="/upload" variant="outlined" size="large" startIcon={<Upload size={18} />}>
                  Submit a groni
                </Button>
              </Box>
              <Button variant="text" onClick={() => setResult(null)} sx={{ mt: 2, color: 'text.secondary' }}>
                Sign up someone else
              </Button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              noValidate
              onSubmit={handleSubmit}
              onFocus={preloadBackend}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: m.duration.base }}
            >
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  {...fieldProps('firstName')}
                  label="First name"
                  autoComplete="given-name"
                  inputProps={{ autoCapitalize: 'words', enterKeyHint: 'next', maxLength: LIMITS.name }}
                />
                <TextField
                  {...fieldProps('lastName')}
                  label="Last name"
                  autoComplete="family-name"
                  inputProps={{ autoCapitalize: 'words', enterKeyHint: 'next', maxLength: LIMITS.name }}
                />
                <TextField
                  {...fieldProps('email')}
                  label="Email"
                  type="email"
                  autoComplete="email"
                  inputProps={{ inputMode: 'email', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, enterKeyHint: 'go', maxLength: LIMITS.email }}
                  sx={{ gridColumn: { sm: 'span 2' } }}
                />
              </Box>
              <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting} sx={{ mt: 3 }}>
                {submitting ? <CircularProgress size={22} color="inherit" aria-label="Joining" /> : 'Join the waitlist'}
              </Button>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2, textAlign: 'center' }}>
                By joining you agree to our{' '}
                <Link component="button" type="button" variant="caption" onClick={() => setPrivacyOpen(true)} sx={{ verticalAlign: 'baseline' }}>
                  Privacy Policy
                </Link>
                . Unsubscribe any time.
              </Typography>
            </motion.form>
          )}
        </AnimatePresence>
      </Box>

      {/* Phones and tablets: a peek at the app below the form. */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 7, textAlign: 'center' }}>
        <Typography variant="overline" color="primary">
          Sneak peek
        </Typography>
        <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
          Your groni diary, ranked.
        </Typography>
        <Mockup name="mockup-feed" alt="The Groni app showing a feed of rated Negronis and a ranked list of bars" sx={{ maxWidth: 420, mx: 'auto' }} />
      </Box>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <Suspense fallback={null}>
        <PrivacyDialog open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      </Suspense>
    </Page>
  );
}

export default WaitlistPage;
